import { Controller } from "@hotwired/stimulus"

const STORAGE_PROVIDERS = {
  local: "localFilesystemConfiguration",
  azure_blob: "azureBlobStorageConfiguration",
  s3: "s3StorageConfiguration"
}

export default class extends Controller {
  static targets = [
    "scheduleHourField",
    "providerReportTypeCheckboxes",
    "useV2Checkbox",
    "v1ExportSelection",
    "v2ExportSelection",
    "storageConfigurationCheckbox",
    "localFilesystemConfiguration",
    "azureBlobStorageConfiguration",
    "s3StorageConfiguration",
    "providerSection",
    "providerSelectedCount"
  ]

  connect() {
    this.toggleScheduleHour()
    this.toggleExportVersion()
    this.toggleStorageConfiguration()
    this.initializeProviderCounts()
  }

  get activeExportSection() {
    return this.useV2CheckboxTarget.checked
      ? this.v2ExportSelectionTarget
      : this.v1ExportSelectionTarget
  }

  toggleStorageConfiguration() {
    const selectedValue = this.storageConfigurationCheckboxTarget.value

    for (const [provider, targetName] of Object.entries(STORAGE_PROVIDERS)) {
      const hasTarget = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTarget]) {
        const shouldShow = selectedValue === provider
        this[`${targetName}Target`].classList.toggle('hidden', !shouldShow)
        this[`${targetName}Target`].querySelectorAll('input').forEach(input => {
          input.disabled = !shouldShow
        })
      }
    }
  }

  toggleScheduleHour() {
    const scheduleType = this.element.querySelector('[data-schedule-type-select]')?.value
    if (this.hasScheduleHourFieldTarget) {
      this.scheduleHourFieldTarget.style.display = scheduleType === 'daily' ? 'block' : 'none'
    }
  }

  // Toggle between V1 and V2 export selection UI
  toggleExportVersion() {
    const useV2 = this.useV2CheckboxTarget.checked

    // Show/hide the V1/V2 report selection containers
    this.v1ExportSelectionTarget.classList.toggle('hidden', useV2)
    this.v2ExportSelectionTarget.classList.toggle('hidden', !useV2)

    // Enable/disable checkboxes based on which version is active
    this.v1ExportSelectionTarget
      .querySelectorAll('input[type="checkbox"]')
      .forEach(cb => {
        cb.disabled = useV2
      })

    this.v2ExportSelectionTarget
      .querySelectorAll('input[type="checkbox"]')
      .forEach(cb => {
        cb.disabled = !useV2
      })

    // Update provider counts for the active version
    this.initializeProviderCounts()
  }

  scheduleTypeChanged() {
    this.toggleScheduleHour()
  }

  selectAllForProvider(event) {
    event.preventDefault()
    event.stopPropagation()
    const provider = event.currentTarget.dataset.provider
    this.setProviderCheckboxes(provider, true)
    this.updateProviderCountForProvider(provider)
  }

  deselectAllForProvider(event) {
    event.preventDefault()
    event.stopPropagation()
    const provider = event.currentTarget.dataset.provider
    this.setProviderCheckboxes(provider, false)
    this.updateProviderCountForProvider(provider)
  }

  setProviderCheckboxes(provider, checked) {
    this.activeExportSection.querySelectorAll(`input[data-provider="${provider}"]`)
      .forEach(cb => {
        cb.checked = checked
      })
  }

  expandAllProviders(event) {
    event.preventDefault()
    this.activeExportSection.querySelectorAll('[data-controller="tip-open"]').forEach(section => {
      const content = section.querySelector('[data-tip-open-target="content"]')
      const icon = section.querySelector('[data-tip-open-target="icon"]')
      if (content) content.classList.remove('hidden')
      if (icon) icon.classList.add('rotate-180')
    })
  }

  collapseAllProviders(event) {
    event.preventDefault()
    this.activeExportSection.querySelectorAll('[data-controller="tip-open"]').forEach(section => {
      const content = section.querySelector('[data-tip-open-target="content"]')
      const icon = section.querySelector('[data-tip-open-target="icon"]')
      if (content) content.classList.add('hidden')
      if (icon) icon.classList.remove('rotate-180')
    })
  }

  updateProviderCount(event) {
    const provider = event.currentTarget.dataset.provider
    this.updateProviderCountForProvider(provider)
  }

  updateProviderCountForProvider(provider) {
    const section = this.activeExportSection

    const countDisplay = section.querySelector(`[data-export-configuration-form-target="providerSelectedCount"][data-provider="${provider}"]`)
    if (!countDisplay) return

    const checkboxes = section.querySelectorAll(`input[data-provider="${provider}"]`)
    const selectedCount = Array.from(checkboxes).filter(cb => cb.checked).length

    const countSpan = countDisplay.querySelector('[data-count]')
    if (countSpan) {
      countSpan.textContent = selectedCount
      countSpan.dataset.count = selectedCount
    }
  }

  initializeProviderCounts() {
    const section = this.activeExportSection

    const providers = new Set()
    section.querySelectorAll('input[data-provider]').forEach(cb => {
      providers.add(cb.dataset.provider)
    })

    providers.forEach(provider => {
      this.updateProviderCountForProvider(provider)
    })
  }
}
